import { zodResolver } from '@hookform/resolvers/zod';
import { clsx } from 'clsx';
import { TFunction } from 'i18next';
import { ComponentProps, useEffect, useRef, useState } from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { z } from 'zod';
import IconButton from '~/components/buttons/NewIconButton';
import Icon from '~/components/data-display/Icon';
import Radio from '~/components/inputs/Radio/Radio';
import RadioGroup from '~/components/inputs/RadioGroup/RadioGroup';
import AmountField from '~/components/inputs/TextField/AmountField';
import TextField from '~/components/inputs/TextField/TextField';
import inputStyles from '~/components/inputs/TextField/TextField.module.scss';
import { HStack, VStack } from '~/components/layout/Stack';
import Popover from '~/components/popovers/Popover/Popover';
import Text from '~/components/typographies/Text';
import { CURRENCIES } from '~/consts/currencies';
import useDataTestId from '~/hooks/useDataTestId';
import IconArrowTrendDown from '~/icons/icon-arrow-trend-down.svg?react';
import IconArrowTrendUp from '~/icons/icon-arrow-trend-up.svg?react';
import IconClose from '~/icons/icon-close.svg?react';
import { TransactionFlowDirection } from '~/types/transaction';
import { formatMoney, newMoney } from '~/utils/money';
import { parseOptionalValue } from '~/utils/numberInputs';
import styles from './AmountFilter.module.scss';
import filtersStyles from './Filters.module.scss';

type Value<WithDirection> = WithDirection extends true
  ? [from: number | null, to: number | null, TransactionFlowDirection | null]
  : [from: number | null, to: number | null];

interface Props<Filters, WithDirection extends boolean> {
  className?: string;
  currencySymbol?: string;
  dataTestId?: string;
  hideLabel?: boolean;
  label?: string;
  name: keyof Filters;
  onChange: (name: keyof Filters, value: Value<WithDirection>) => void;
  placeholder?: string;
  popoverPlacement?: ComponentProps<typeof Popover>['placement'];
  showFlowDirection?: WithDirection;
  value: Value<WithDirection>;
}

const schema = z.object({
  from: z.string(),
  to: z.string(),
  flowDirection: z.nativeEnum(TransactionFlowDirection).nullable(),
});

type Form = z.infer<typeof schema>;

const parseValue = (value: number | null): string => (value === null ? '' : value.toString());

const areValueAndFormEqual = (externalValue: Value<true | false>, formValue: Form): boolean => {
  const [from, to, flowDirection] = externalValue;
  return (
    parseOptionalValue(formValue.from) === from &&
    parseOptionalValue(formValue.to) === to &&
    formValue.flowDirection === flowDirection
  );
};

const getDisplayValue = (
  t: TFunction<'inputs', 'amount_filter'>,
  from: number | null,
  to: number | null,
  flowDirection: TransactionFlowDirection | null,
) => {
  const formattedFrom = from ? formatMoney(newMoney(from)) : null;
  const formattedTo = to ? formatMoney(newMoney(to)) : null;

  const rangeValue =
    formattedFrom && formattedTo
      ? t('display_value.from_to', { from: formattedFrom, to: formattedTo })
      : formattedFrom
        ? t('display_value.from', { from: formattedFrom })
        : formattedTo
          ? t('display_value.to', { to: formattedTo })
          : null;

  if (rangeValue) return rangeValue;

  if (flowDirection === TransactionFlowDirection.Inflow) return t('display_value.inflow');
  if (flowDirection === TransactionFlowDirection.Outflow) return t('display_value.outflow');

  return '';
};

function useSyncFormWithProps<Filters, WithDirection extends boolean>(
  name: Props<Filters, WithDirection>['name'],
  externalValue: Value<WithDirection>,
  onChange: Props<Filters, WithDirection>['onChange'],
  { watch, reset, getValues }: Pick<UseFormReturn<Form>, 'watch' | 'reset' | 'getValues'>,
) {
  useEffect(() => {
    const formValue = getValues();
    if (areValueAndFormEqual(externalValue, formValue)) return;

    reset({
      from: parseValue(externalValue[0]),
      to: parseValue(externalValue[1]),
      flowDirection: externalValue[2],
    });
  }, [externalValue, getValues, reset]);

  const debouncedOnChange = useDebouncedCallback((values: Value<WithDirection>) => {
    const normalizedValues = [values[0], values[1]] as Value<WithDirection>;
    if (values[2] !== undefined) normalizedValues[2] = values[2];
    onChange(name, normalizedValues);
  }, 500);

  const { from, to, flowDirection } = watch();
  const prevValue = useRef<Value<WithDirection>>(externalValue);

  useEffect(() => {
    if (areValueAndFormEqual(prevValue.current, { from, to, flowDirection })) return;
    const currentValue = [parseOptionalValue(from), parseOptionalValue(to), flowDirection] as Value<WithDirection>;
    debouncedOnChange(currentValue);
    if (flowDirection !== prevValue.current[2]) {
      debouncedOnChange.flush();
    }
    prevValue.current = currentValue;
  }, [from, to, flowDirection, debouncedOnChange, name, onChange]);
}

const DirectionIcon = ({ flowDirection }: { flowDirection: TransactionFlowDirection | null }) => {
  if (flowDirection === TransactionFlowDirection.Inflow)
    return <Icon component={IconArrowTrendUp} className={clsx(styles.trendingUp, inputStyles.icon)} />;

  if (flowDirection === TransactionFlowDirection.Outflow)
    return <Icon component={IconArrowTrendDown} className={clsx(styles.trendingDown, inputStyles.icon)} />;

  return null;
};

const AmountFilter = <Filters, WithDirection extends boolean = false>({
  className,
  currencySymbol = CURRENCIES.EUR.symbol,
  dataTestId,
  hideLabel,
  label,
  name,
  onChange,
  placeholder,
  popoverPlacement,
  showFlowDirection,
  value,
}: Props<Filters, WithDirection>) => {
  const { t } = useTranslation('inputs', { keyPrefix: 'amount_filter' });
  const fromRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const dt = useDataTestId(dataTestId);
  const { control, watch, reset, getValues } = useForm<Form>({
    defaultValues: { from: parseValue(value[0]), to: parseValue(value[1]), flowDirection: value[2] },
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  useSyncFormWithProps(name, value, onChange, { watch, reset, getValues });

  const { from: actualFrom, to: actualTo, flowDirection } = watch();
  const [from, to] = [parseOptionalValue(actualFrom), parseOptionalValue(actualTo)];

  const displayValue = getDisplayValue(t, from, to, flowDirection);
  const DirectionInputIcon = () => <DirectionIcon flowDirection={flowDirection} />;

  return (
    <>
      <TextField
        className={clsx(filtersStyles.amountFilterControl, className)}
        dataTestId={dt('input')}
        value={displayValue}
        label={label ?? t('label')}
        hideLabel={hideLabel}
        placeholder={placeholder ?? label ?? t('label')}
        isReadOnly
        onClick={() => setIsOpen(true)}
        Icon={DirectionInputIcon}
        endAddon={
          (from || to) && (
            <IconButton
              data-testid={dt('clear')}
              label={t('clear')}
              className={filtersStyles.clearButton}
              onClick={() => {
                reset({ from: '', to: '', flowDirection: showFlowDirection ? null : undefined });
              }}
              size="small"
              Icon={IconClose}
              variant="ghost"
            />
          )
        }
        ref={fromRef}
      />
      {isOpen && (
        <Popover isOpen triggerRef={fromRef} onOpenChange={setIsOpen} placement={popoverPlacement}>
          <VStack gap={3} className={styles.popover}>
            {showFlowDirection && (
              <VStack gap={2}>
                <Text variant="body2" weight="semibold">
                  {t('flow_direction.label')}
                </Text>
                <Controller
                  control={control}
                  name="flowDirection"
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      ariaLabel={t('flow_direction.label')}
                      onChange={(value) => field.onChange(value === 'All' ? null : value)}
                      value={field.value ?? 'All'}
                    >
                      <Radio value={'All'}>{t('flow_direction.all')}</Radio>
                      <Radio value={TransactionFlowDirection.Outflow}>{t('flow_direction.outflow')}</Radio>
                      <Radio value={TransactionFlowDirection.Inflow}>{t('flow_direction.inflow')}</Radio>
                    </RadioGroup>
                  )}
                />
              </VStack>
            )}
            <HStack gap={2}>
              <Controller
                control={control}
                name="from"
                render={({ field, fieldState }) => (
                  <AmountField
                    {...field}
                    errorMessage={fieldState.error?.message}
                    isInvalid={fieldState.invalid}
                    onlyPositive
                    startAddon={currencySymbol}
                    name="from"
                    label={t('amount_from')}
                    maxDecimalPlaces={2}
                  />
                )}
              />
              <Controller
                control={control}
                name="to"
                render={({ field, fieldState }) => (
                  <AmountField
                    {...field}
                    errorMessage={fieldState.error?.message}
                    isInvalid={fieldState.invalid}
                    onlyPositive
                    startAddon={currencySymbol}
                    name="to"
                    label={t('amount_to')}
                    maxDecimalPlaces={2}
                  />
                )}
              />
            </HStack>
          </VStack>
        </Popover>
      )}
    </>
  );
};

export default AmountFilter;
